import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { ProductExtendedClient } from '@frontend/product/api';
import { ProductExtended } from '@frontend/product/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAuditTransaction } from '../../../hooks';
import { useAppSelector } from '../../../redux/store';
import { useKioskNavigate } from '../../../utils/kiosk-navigation';

export const useProductList = () => {
    const intl = useIntl();
    const navigate = useKioskNavigate();
    const navigationState = useAppSelector((store) => store.navigation);
    const { slotId } = useParams();
    const spot = navigationState.spot;
    const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
    const [selectedProduct, setSelectedProduct] = useState<ProductExtended | null>(null);
    const [slot, setSlot] = useState<Slot | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchValue, setSearchValue] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('search');

    const { isSubmitting, error, setError, createTransactionWithPackage } = useAuditTransaction({
        id: 'PRODUCT_LIST',
        application: Application.SPOTS,
        feature: Feature.LAYOUT,
        slot,
        onComplete: () => navigate(-1)
    });

    useEffect(() => {
        if (!slotId || !spot || !accountId) {
            setIsLoading(false);
            if (!slotId) setError(CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.SLOT.DEFAULT));
            return;
        }
        SlotClient.fetchAccountSpotSlot(accountId, spot.id, slotId)
            .then((slot) => {
                setSlot(slot);
            })
            .catch(() => {
                setError(CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.SLOT.DEFAULT));
                setSlot(null);
            })
            .finally(() => setIsLoading(false));
    }, [slotId, spot, accountId, setError]);

    const fetch = useCallback(
        (arg?: ApiQueryParams<DefaultQueryParams>): Promise<PaginatedResponse<ProductExtended>> =>
            ProductExtendedClient.fetchExtendedProducts({
                ...arg,
                [searchKey]: searchValue || undefined
            }),
        [searchValue, searchKey]
    );

    const handleSearchChange = useCallback((value: string, filterKey: string | null) => {
        setSearchValue(value);
        if (filterKey) setSearchKey(filterKey);
    }, []);

    const handleProductClick = useCallback((product: ProductExtended) => {
        setSelectedProduct(product);
    }, []);

    const isProductSelected = useCallback((product: ProductExtended) => selectedProduct?.id === product.id, [selectedProduct]);

    const handleProductAdd = () => {
        if (!selectedProduct) return;
        createTransactionWithPackage({
            accountId: selectedProduct.account_id,
            productId: selectedProduct.id
        });
    };

    const handleChooseEquipment = () => {
        if (!selectedProduct) return;
        navigate(`./${selectedProduct.id}/add-equipment`);
    };

    return {
        navigate,
        selectedProduct,
        setSelectedProduct,
        fetch,
        handleProductClick,
        isProductSelected,
        handleProductAdd,
        isLoading,
        isSubmitting,
        error,
        hasError: !!error && !slot,
        slot,
        handleChooseEquipment,
        searchValue,
        searchKey,
        handleSearchChange,
        intl
    };
};
